import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateRequest } from "../../api/developmentRequests";
import BackButton from "../../components/ui/BackButton";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Field } from "../../components/ui/Field";
import { FilterBar } from "../../components/ui/FilterBar";
import { Form } from "../../components/ui/Form";
import { Page } from "../../components/ui/Page";
import { Switch } from "../../components/ui/Switch";

import { DepartmentSelect } from "../../components/ui/DepartmentSelect";
import { UserSelect } from "../../components/ui/UserSelect";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import type { UserLightResponse } from "../../types/user";
import { hasTrimmedValue } from "../../utils/formValidation";
import styles from "./NewRequestPage.module.css";

interface RequestFormValues {
  title: string;
  description: string;
  audience: "internal" | "external";
  departments: string[];
  needsErpIntegration: boolean;
  participants: UserLightResponse[];
}

export default function NewRequestPage() {
  const { t } = useTranslation();
  useLayoutTitle(t("requests.newRequest"));
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const createRequest = useCreateRequest();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RequestFormValues>({
    defaultValues: {
      title: "",
      description: "",
      audience: "internal",
      departments: [],
      needsErpIntegration: false,
      participants: [],
    },
  });

  const onSubmit = handleSubmit((values) => {
    createRequest.mutate(
      {
        title: values.title.trim(),
        description: values.description.trim(),
        audience: values.audience,
        departments: values.departments,
        needsErpIntegration: values.needsErpIntegration,
        participantIds: values.participants.map((p) => p.id),
      },
      {
        onSuccess: (res) => {
          navigate(`/requests/${res.id}`);
        },
      },
    );
  });

  return (
    <Page width="medium">
      <Card>
        <BackButton
          label={t("requests.backToRequests")}
          onClick={() => navigate("/requests")}
          className={styles.backButton}
        />
        <Form noValidate onSubmit={onSubmit}>
          <Field.Input
            label={t("requests.title")}
            error={errors.title?.message}
            {...register("title", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />

          <Field.Textarea
            label={t("requests.description")}
            info={t("common.descriptionHint")}
            infoTitle={t("requests.description")}
            error={errors.description?.message}
            {...register("description", {
              validate: (value) => hasTrimmedValue(value) || t("common.required"),
            })}
          />

          <Form.Field
            label={t("requests.audience")}
            infoTitle={t("requests.audience")}
            info={
              <>
                <p>
                  <strong>{t("requests.audienceInternal")}</strong> — {t("requests.audienceInternalInfo")}
                </p>
                <p>
                  <strong>{t("requests.audienceExternal")}</strong> — {t("requests.audienceExternalInfo")}
                </p>
              </>
            }
          >
            <Controller
              name="audience"
              control={control}
              render={({ field }) => (
                <FilterBar
                  value={field.value}
                  options={[
                    { value: "internal", label: t("requests.audienceInternal") },
                    { value: "external", label: t("requests.audienceExternal") },
                  ]}
                  onChange={field.onChange}
                />
              )}
            />
          </Form.Field>

          <Controller
            name="departments"
            control={control}
            render={({ field }) => (
              <DepartmentSelect label={t("requests.departments")} selected={field.value} onChange={field.onChange} />
            )}
          />

          <Switch
            label={t("requests.needsErpIntegration")}
            info={t("requests.needsErpIntegrationInfo")}
            infoTitle={t("requests.needsErpIntegration")}
            {...register("needsErpIntegration")}
          />

          <Form.Field
            label={t("requests.participants")}
            infoTitle={t("requests.participants")}
            info={t("requests.participantsInfo")}
          >
            <Controller
              name="participants"
              control={control}
              render={({ field }) => (
                <UserSelect
                  selected={field.value}
                  onChange={field.onChange}
                  excludeIds={currentUser ? [currentUser.id] : []}
                />
              )}
            />
          </Form.Field>

          {createRequest.isError && <Form.Feedback type="error">{t("common.error")}</Form.Feedback>}

          <Form.Actions>
            <Button type="button" variant="secondary" onClick={() => navigate("/requests")}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={createRequest.isPending}>
              {t("common.submit")}
            </Button>
          </Form.Actions>
        </Form>
      </Card>
    </Page>
  );
}
